import { useEffect, useRef, useState } from 'react';
import {
  buildAggregatedViewInitOptions,
  ViewerExtension,
} from 'src/components/viewers/common/utils/autodesk';

export type AggregatedView = Autodesk.Viewing.AggregatedView;

const originalFetch = window.fetch;

function setupFetchInterception() {
  // Override fetch to handle presigned URL conflicts for SheetsViewerV2 (APS Viewer)
  // We assume that only fetch is used by Autodesk here, and that other methods are not used
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
    let url = typeof input === 'string' ? input : input.toString();

    // Clean up URLs that have been modified by Autodesk viewer
    // The viewer appends ?domain= to the x-id parameter value
    if (url.includes('X-Amz-Algorithm') && url.includes('?domain=')) {
      // We assume that the x-id value is the only one that needs to be cleaned up
      // and that the rest of the URL is not important
      try {
        const urlObj = new URL(url);
        const xIdValue = urlObj.searchParams.get('x-id');
        if (xIdValue?.includes('?domain=')) {
          const cleanXId = xIdValue.split('?domain=')[0];
          urlObj.searchParams.set('x-id', cleanXId);
          url = urlObj.toString();
          input = url;
        }
      } catch (error) {
        // Fallback: remove everything before ?domain=
        const domainIndex = url.indexOf('?domain=');
        if (domainIndex !== -1) {
          url = url.substring(0, domainIndex);
          input = url;
        }
      }
    }

    // Remove Authorization headers for presigned URLs to prevent S3 conflicts
    if (url.includes('X-Amz-Algorithm') && init?.headers) {
      const headers = new Headers(init.headers);
      headers.delete('Authorization');
      init.headers = headers;
    }

    return originalFetch.call(this, input, init);
  };
}

export function useSheetsAggregatedView() {
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const [aggregatedView, setAggregatedView] = useState<AggregatedView | null>(
    null
  );

  useEffect(() => {
    if (viewerRef.current === null) return;

    const view = new Autodesk.Viewing.AggregatedView();

    view
      .init(viewerRef.current, buildAggregatedViewInitOptions())
      .then(() =>
        Promise.all([
          view.viewer.loadExtension(ViewerExtension.Snapping),
          view.viewer.loadExtension(ViewerExtension.PDF),
          view.viewer.loadExtension(ViewerExtension.Edit2D),
          view.viewer.loadExtension(ViewerExtension.SceneBuilder),
        ])
      )
      .then(() => {
        view.viewer.disableSelection(true);
        view.viewer.disableHighlight(true);
        // Disable built-in context menu - we use our own custom context menu
        view.viewer.setContextMenu(null);

        setupFetchInterception();

        setAggregatedView(view);
      });
  }, []);

  return { viewerRef, aggregatedView };
}
